import { apiCall } from "../../api/ApiResult.model";
import { CognniApp, Env } from "../../models";
import {
  CmsGlossaryItem,
  CmsPartner,
  CmsPremiumFeature,
  CmsPricingPlan,
  CmsAsset,
  CmsReleaseNotesItem,
  CmsUseCase,
  CmsOrgAlertMsg,
  CmsMipVLabelsDefault,
  CmsLabelPolicy,
  CmsWalkthrough,
} from "./cms.models";
import { env } from "@/env";

export const cms = <R, T>(query: (env: Env, input: R) => string) =>
  apiCall<R, T>("get-apps", async (arg: R) => {
    const _env = await env();
    const q = query(_env, arg);
    const parsedQuery = encodeURIComponent(q);
    const req = await fetch(
      `https://lax6ynte.apicdn.sanity.io/v1/data/query/production?query=${parsedQuery}`
    );
    const res = await req.json();
    if (req.status !== 200) {
      throw { response: { status: req.status } };
    } else {
      return {
        success: true,
        value: res.result,
      };
    }
  });

const get_assets = cms<void, CmsAsset[]>(
  (env) => `
  *[_type=="assets"]{
    ...,
    "img": img.asset->url,
    "video_url": video_url.asset->url,
  }
  `
);

const get_premium_features = cms<void, CmsPremiumFeature[]>(
  (env) => `
*[_type=="premium_features"]{
  ...,
  "video_poster": video_poster.asset->url,
  "video_file": video_file.asset->url,
}
`
);

const get_apps = cms<void, CognniApp[]>(
  (env) => `
*[_type=="app" && available == true]{
  ...,
  "icon": icon.asset->url
}
`
);

const get_walkthrough = cms<{ id: string }, CmsWalkthrough[]>(
  (env, { id }) => `
*[_type=="walkthrough" && id == "${id}"]{
  ...,
  steps[]{
    ...,
    "gif": gif.asset->url
  }
}
`
);

const get_use_cases = cms<void, CmsUseCase[]>(
  (env) => `
*[_type=="usecase" && available == true]{
  ...,
  "poster": poster.asset->url
}
`
);

const get_pricing_plans = cms<void, CmsPricingPlan[]>(
  (env) => `
*[_type=="pricing_plan" && env >= ${env.env_num} ]{
  ...,
  "icon": icon.asset->url,
  "video_poster": video_poster.asset->url,
  "video_file": video_file.asset->url,
}
`
);

const get_glossary = cms<void, CmsGlossaryItem[]>(
  (env) => `
*[_type=="glossary"]{
  ...
}
`
);

const get_benchmark = cms<void, CmsGlossaryItem[]>(
  (env) => `
*[_type=="grades"]{
  id,
  name,
  max,
  min
}
`
);

const get_release_notes = cms<void, CmsReleaseNotesItem[]>(
  (env) => `
*[_type=="release_notes"] | order(release_date desc){
  ...,
  notes[]{
    ...,
    "image": image.asset->url,
    "video_poster": video_poster.asset->url,
    "gif": image.asset->url,
  }
}
`
);

const get_mip_vlabels_defaults = cms<void, CmsMipVLabelsDefault[]>(
  (env) => `
*[_type=="mip_vlabels_defaults"]{
  labelId,
  name,
  automate,
  override,
  confidenceThreshold
}
`
);

const get_partner_data = async (partnerId: string) => {
  return await cms<void, CmsPartner[]>(
    (env) => `
  *[_type=="partners" && id=="${partnerId}"]{
    ...,
    "icon": icon.asset->url,
    "original_icon": original_icon.asset->url,
  }
  `
  )();
};

const get_org_alter_msg = async (org_name: string) => {
  return await cms<void, CmsOrgAlertMsg[]>(
    (env) => `
  *[_type=="clients_alert_msgs" && id=="${org_name}"]{
    ...
  }
  `
  )();
};

const get_info_types_policies = cms<void, CmsLabelPolicy[]>(
  (env) => `
  *[_type=="label_policies" && env >= ${env.env_num}]{
    id,
    env,
    name,
    desc,
    group,
    policies_scripts_ids
  }
`
);
export const CMS = {
  get_apps,
  get_walkthrough,
  get_org_alter_msg,
  get_premium_features,
  get_use_cases,
  get_assets,
  get_pricing_plans,
  get_partner_data,
  get_glossary,
  get_benchmark,
  get_release_notes,
  get_mip_vlabels_defaults,
  get_info_types_policies,
};
